import type { AchievementProgress } from '../../api/models';
import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { Strings } from '../../strings';
import { SymbolView } from '../../symbols/SymbolView';

import { useAchievements } from './useAchievements';

/**
 * What the child has earned, and the two or three things within reach.
 * Mirrors android/…/Screens/Achievements/AchievementsScreen.kt.
 *
 * Three rules the design system makes here, all of them load-bearing:
 *
 * **The number shown is the *best* streak, never the current one.** A current streak falls to zero after
 * an illness; making that the headline would turn the reward system into a punishment, which
 * 09-Rewards-And-Island §6 forbids outright.
 *
 * **An unearned achievement shows its distance, not a lock.** "5 z 7" is a goal; a locked box is a refusal.
 *
 * **Nothing here compares this child to any other.** No rank, no percentage of children, no leaderboard.
 * A badge says what *this* child did.
 */
export function AchievementsScreen({
  childId = null,
}: {
  childId?: string | null;
  /**
   * Only the parent's view passes one. In child mode this screen is a tab and the bottom navigation is
   * the way out; for a parent it was a dead end, reachable from a dashboard with no route back to it
   * (R0110).
   */
}) {
  const model = useAchievements(childId);
  const overview = model.state.overview;
  const earned = (overview?.achievements ?? []).filter((one: AchievementProgress): boolean => one.isEarned);
  const nextUp = overview?.nextUp ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* "Moje ocenenia" is the child's voice. A parent reading their child's screen gets the neutral
          title — same screen, same data, and nobody is addressed as somebody they are not. */}

      <AppText as="h1" style="titleL">
        {childId === null ? Strings.childHomeAchievements : Strings.overviewAchievements}
      </AppText>

      {/* Never an error tone: this screen is shown to a child, and there is no red in child mode. */}
      {model.state.error !== null ? (
        <RetryNotice text={model.state.error} tone="info" onRetry={() => void model.load()} />
      ) : null}

      {overview !== null ? (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Figure
              label={Strings.achievementsEarned}
              value={`${overview.earnedCount} / ${overview.totalCount}`}
            />
            <Figure label={Strings.achievementsBestStreak} value={String(overview.bestStreakDays)} />
          </div>

          {earned.length === 0 ? (
            <AppText color="var(--text-secondary)">{Strings.achievementsEmpty}</AppText>
          ) : (
            earned.map((achievement: AchievementProgress) => (
              <EarnedRow key={achievement.code} achievement={achievement} />
            ))
          )}

          {nextUp.length > 0 ? (
            <>
              <AppText as="h2" style="titleM">
                {Strings.achievementsNextUp}
              </AppText>

              {nextUp.map((achievement: AchievementProgress) => (
                <NextUpRow key={achievement.code} achievement={achievement} />
              ))}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: 1,
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <AppText style="titleM" color="var(--action-primary)">
        {value}
      </AppText>
      <AppText style="bodyS" color="var(--text-secondary)">
        {label}
      </AppText>
    </div>
  );
}

function EarnedRow({ achievement }: { achievement: AchievementProgress }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        borderRadius: 'var(--radius-l)',
        background: 'var(--surface-reward)',
        padding: 'var(--space-4)',
      }}
    >
      <SymbolView token={achievement.symbol} size={40} color="var(--action-primary)" />

      <div style={{ flex: 1 }}>
        <AppText style="titleS">{achievement.name}</AppText>
        <AppText style="bodyS" color="var(--text-secondary)">
          {achievement.description}
        </AppText>
      </div>
    </div>
  );
}

/**
 * An achievement not yet earned, with how far along the child is.
 *
 * Dimmed rather than crossed out, and it carries the same description as the earned form — the child is
 * being shown what they are working towards, not what they failed to get.
 */
function NextUpRow({ achievement }: { achievement: AchievementProgress }) {
  const fraction =
    achievement.threshold <= 0 ? 0 : Math.min(1, Math.max(0, achievement.current / achievement.threshold));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        borderRadius: 'var(--radius-l)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-default)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <span style={{ opacity: 0.6, display: 'inline-flex' }}>
          <SymbolView token={achievement.symbol} size={32} color="var(--text-secondary)" />
        </span>

        <div style={{ flex: 1 }}>
          <AppText style="titleS">{achievement.name}</AppText>
          <AppText style="bodyS" color="var(--text-secondary)">
            {achievement.description}
          </AppText>
        </div>

        <AppText as="span" style="numeric" color="var(--text-secondary)">
          {achievement.current} / {achievement.threshold}
        </AppText>
      </div>

      <div
        role="progressbar"
        aria-valuenow={achievement.current}
        aria-valuemin={0}
        aria-valuemax={achievement.threshold}
        style={{
          width: '100%',
          height: 8,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--progress-track)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            width: `${fraction * 100}%`,
            height: 8,
            borderRadius: 'var(--radius-pill)',
            background: 'var(--progress-fill)',
          }}
        />
      </div>
    </div>
  );
}
